import { useEffect, useState } from 'react'
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { Box, Container, Typography, Button, TextField, Alert } from '@mui/material'
import { authService } from '../services/auth-service'
import { SiteNav } from '../components/site/SiteNav'
import { SiteFooter } from '../components/site/SiteFooter'

/**
 * Password reset landing page.
 *
 * The reset email links here and this route did not exist, so anyone who
 * forgot their password hit "Not Found" and had no way back into their
 * account at all.
 */
export const Route = createFileRoute('/reset-password')({
  component: ResetPassword,
})

function ResetPassword() {
  const navigate = useNavigate()
  const [token, setToken] = useState('')
  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [done, setDone] = useState(false)

  useEffect(() => {
    setToken(new URLSearchParams(window.location.search).get('token') ?? '')
  }, [])

  // Checked here as well as server-side so the mismatch is caught before a
  // round trip, rather than after.
  const mismatch = confirm.length > 0 && password !== confirm

  const submit = async () => {
    setBusy(true)
    setError(null)
    try {
      await authService.resetPassword(token, password, confirm)
      setDone(true)
      setTimeout(() => navigate({ to: '/login' }), 2500)
    } catch (e: unknown) {
      setError(e instanceof Error && e.message ? e.message : 'Could not reset your password.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <Box sx={{ minHeight: '100vh', background: 'var(--bg)', display: 'flex', flexDirection: 'column' }}>
      <SiteNav />
      <Container maxWidth="sm" sx={{ flex: 1, py: { xs: 8, md: 12 } }}>
        <Typography component="h1" sx={{
          fontSize: '1.9rem', fontWeight: 700, letterSpacing: '-0.03em', mb: 1.5,
        }}>
          {done ? 'Password changed.' : 'Choose a new password'}
        </Typography>

        {done ? (
          <>
            <Typography sx={{ color: 'var(--text-secondary)', mb: 4 }}>
              You can sign in with it now. Taking you there.
            </Typography>
            <Button component={Link} to="/login" variant="contained">Sign in</Button>
          </>
        ) : !token ? (
          <>
            <Typography sx={{ color: 'var(--text-secondary)', mb: 4 }}>
              This page needs the link from your password reset email. Reset
              links expire, so request a fresh one if yours is old.
            </Typography>
            <Button component={Link} to="/login" variant="contained">Back to sign in</Button>
          </>
        ) : (
          <>
            <Typography sx={{ color: 'var(--text-secondary)', mb: 4 }}>
              Pick something you don&rsquo;t use anywhere else.
            </Typography>

            {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

            <Box sx={{ display: 'grid', gap: 2.5, maxWidth: 380 }}>
              <TextField
                label="New password" type="password" size="small" fullWidth
                value={password} onChange={(e) => setPassword(e.target.value)}
                autoComplete="new-password"
                helperText="At least 8 characters, with a number and a symbol"
              />
              <TextField
                label="Confirm new password" type="password" size="small" fullWidth
                value={confirm} onChange={(e) => setConfirm(e.target.value)}
                autoComplete="new-password"
                error={mismatch}
                helperText={mismatch ? 'These do not match' : ' '}
              />
              <Box>
                <Button
                  variant="contained" onClick={submit}
                  disabled={busy || !password || mismatch || password !== confirm}
                >
                  {busy ? 'Saving…' : 'Set new password'}
                </Button>
              </Box>
            </Box>
          </>
        )}
      </Container>
      <SiteFooter />
    </Box>
  )
}
